import { FC, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { InputGroup, InputRightElement } from '@chakra-ui/react';

import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';
import { getFortyFiveDaysEndOfMonth } from 'features/Work/utils';

import locales from './locales';

type DelayOption = number | 'fortyFiveEndOfMonth' | 'other';

const DELAY_OPTIONS = [
  { label: locales.noDelay, value: 0 },
  { label: `7 ${locales.days}`, value: 7 },
  { label: `14 ${locales.days}`, value: 14 },
  { label: `15 ${locales.days}`, value: 15 },
  { label: `21 ${locales.days}`, value: 21 },
  { label: `30 ${locales.days}`, value: 30 },
  { label: locales.fortyFiveEndOfMonth, value: 'fortyFiveEndOfMonth' },
  { label: locales.other, value: 'other' },
] satisfies { label: string; value: DelayOption }[];

interface PaymentPeriodInputProps {
  value: number;
  onChange: (value: number | string) => void;
}
const PaymentPeriodInput: FC<PaymentPeriodInputProps> = ({
  onChange,
  value,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { getValues } = useFormContext<InvoiceFormSchema | QuoteFormSchema>();

  const [internalValue, setInternalValue] = useState<number | string>(value);

  const [isCustomInput, setIsCustomInput] = useState(
    !DELAY_OPTIONS.map((option) => option.value).includes(value),
  );

  const onSelect: (v: DelayOption | null) => void = (delay) => {
    if (delay === null) {
      return;
    }

    if (typeof delay === 'number') {
      onChange(delay);
    }

    if (delay === 'fortyFiveEndOfMonth') {
      const startAt = getValues('startAt');
      const fortyFiveDaysEndOfMonth = getFortyFiveDaysEndOfMonth(startAt);

      setInternalValue(fortyFiveDaysEndOfMonth);
      setIsCustomInput(true);
      onChange(fortyFiveDaysEndOfMonth);
    }

    if (delay === 'other') {
      setIsCustomInput(true);

      /**
       * use a setTimeout because we have to wait for the input to be rendered
       */
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const onInputValueChange = (val: string | number) => {
    setInternalValue(val);
    onChange(val || 0);
  };

  const onCloseCustomInput = () => {
    setIsCustomInput(false);
    onChange(30);
  };

  return (
    <>
      {isCustomInput ? (
        <InputGroup>
          <Form.Input
            onChange={({ target }) =>
              onInputValueChange(
                target.value === '' ? target.value : Number(target.value),
              )
            }
            ref={inputRef}
            type="number"
            value={internalValue ?? undefined}
          />

          <InputRightElement paddingRight={0}>
            <IconButton
              aria-label=""
              icon="cancel"
              iconSize="icon-20"
              onClick={onCloseCustomInput}
              size="sm"
              variant="inline-secondary"
            />
          </InputRightElement>
        </InputGroup>
      ) : (
        <Form.SunshineSelect
          onChange={(option) => onSelect(option?.value ?? null)}
          options={DELAY_OPTIONS}
          value={DELAY_OPTIONS.find((option) => option.value === value)}
        />
      )}
    </>
  );
};

export default PaymentPeriodInput;
